import { useEffect, type PropsWithChildren, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { IconX } from "@tabler/icons-react";

/**
 * Reusable modal overlay for companion editing and booking selection.
 * Uses only inline styles (no Tailwind dependency in runtime).
 * Renders via portal to escape stacking contexts.
 */
export type FormModalStyleOverrides = {
  modalBackgroundColor?: string | undefined;
  modalBorderRadius?: number | undefined;
  modalMaxWidth?: number | undefined;
  modalTitleColor?: string | undefined;
  modalTitleFontSize?: string | undefined;
  modalFooterBorderColor?: string | undefined;
  modalBackdropOpacity?: number | undefined;
  closeIconColor?: string | undefined;
  modalBorderColor?: string | undefined;
};

export function FormModal({
  open,
  onClose,
  title,
  children,
  footer,
  styleOverrides,
}: PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title?: string | undefined;
  footer?: ReactNode | undefined;
  styleOverrides?: FormModalStyleOverrides | undefined;
}>) {
  // Escape key handler
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <motion.div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: `rgba(0,0,0,${(styleOverrides?.modalBackdropOpacity ?? 50) / 100})`,
            }}
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Card */}
          <motion.div
            style={{
              position: "relative",
              backgroundColor: styleOverrides?.modalBackgroundColor || "var(--wb-background, #fff)",
              border: styleOverrides?.modalBorderColor
                ? `1px solid ${styleOverrides.modalBorderColor}`
                : undefined,
              borderRadius: styleOverrides?.modalBorderRadius ?? 12,
              padding: 24,
              maxWidth: styleOverrides?.modalMaxWidth ?? 560,
              width: "calc(100% - 32px)",
              minWidth: 320,
              maxHeight: "85vh",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {title ? (
                <h3
                  style={{
                    margin: 0,
                    fontSize: styleOverrides?.modalTitleFontSize || "1rem",
                    fontWeight: 600,
                    color: styleOverrides?.modalTitleColor || "var(--wb-primary, #121212)",
                  }}
                >
                  {title}
                </h3>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={onClose}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: styleOverrides?.closeIconColor || "var(--wb-secondary, #64748b)",
                  padding: 4,
                }}
              >
                <IconX size={20} />
              </button>
            </div>

            {/* Body */}
            <div>{children}</div>

            {/* Footer */}
            {footer ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  paddingTop: 8,
                  borderTop: styleOverrides?.modalFooterBorderColor
                    ? `1px solid ${styleOverrides.modalFooterBorderColor}`
                    : "1px solid color-mix(in srgb, var(--wb-secondary, #e2e8f0) 30%, transparent)",
                }}
              >
                {footer}
              </div>
            ) : null}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
